import { Settlement } from 'common/src/utils/types';
import {
  ExporterCostCategory,
  SettlementExporterCost,
  UNCLASSIFIED_EXPORTER_COST_LABEL,
} from 'common/src/api/exporter-cost-categories';

type ExportRow = Record<string, string | number | null>;

/**
 * Rewrites detail rows (built in settlement → detail order by
 * prepareSettlementDetailsData) to the Estándar Exportadora: the FruitMarkets
 * cost columns (commission, tax, other costs, repacking, freight and CIF) are
 * replaced, in place, by one column per exporter category in local currency
 * and in USD, and FOB is recalculated from those costs.
 */
export const toExporterCostStandardRows = (
  rows: ExportRow[],
  settlements: Settlement[],
  costs: SettlementExporterCost[],
  categories: ExporterCostCategory[],
  t: (key: string) => string
): ExportRow[] => {
  const costsByDetail = new Map<number, SettlementExporterCost[]>();
  costs.forEach((cost) => {
    const list = costsByDetail.get(cost.settlementDetailId) || [];
    list.push(cost);
    costsByDetail.set(cost.settlementDetailId, list);
  });

  const usedCategoryIds = new Set(costs.map((cost) => cost.categoryId));
  const columns = [
    ...categories
      .filter((category) => usedCategoryIds.has(category.id))
      .map((category) => ({
        id: category.id as number | null,
        name: category.name,
      })),
    ...(usedCategoryIds.has(null)
      ? [{ id: null, name: t(UNCLASSIFIED_EXPORTER_COST_LABEL) }]
      : []),
  ];

  const firstLocalCost = t('Comisión Moneda Local');
  const firstUsdCost = t('Comisión USD');
  const fobLocal = t('FOB Moneda Local');
  const fobUsd = t('FOB USD');
  const replacedColumns = new Set([
    t('Comisión Moneda Local'),
    t('Impuestos Moneda Local'),
    t('Otros Costos Moneda Local'),
    t('Repacking Moneda Local'),
    t('CIF Moneda Local'),
    t('Flete Moneda Local'),
    t('Comisión USD'),
    t('Impuestos USD'),
    t('Otros Costos USD'),
    t('Repacking USD'),
    t('CIF USD'),
    t('Flete USD'),
  ]);

  const details = settlements.flatMap(
    (settlement) => settlement.settlement_details
  );

  return rows.map((row, index) => {
    const detail = details[index];
    if (!detail) return row;
    const detailCosts = costsByDetail.get(detail.id) || [];
    const exchangeRate = detail.exchange_rate || 1;
    const sumFor = (
      categoryId: number | null,
      field: 'amountLocal' | 'amountUsd'
    ) =>
      detailCosts
        .filter((cost) => cost.categoryId === categoryId)
        .reduce((sum, cost) => sum + cost[field], 0);
    const totalLocal = detailCosts.reduce(
      (sum, cost) => sum + cost.amountLocal,
      0
    );
    const totalUsd = detailCosts.reduce((sum, cost) => sum + cost.amountUsd, 0);

    const next: ExportRow = {};
    Object.entries(row).forEach(([key, value]) => {
      if (key === firstLocalCost) {
        columns.forEach((column) => {
          next[`${column.name} ${t('Moneda Local')}`] = sumFor(
            column.id,
            'amountLocal'
          );
        });
      }
      if (key === firstUsdCost) {
        columns.forEach((column) => {
          next[`${column.name} USD`] = sumFor(column.id, 'amountUsd');
        });
      }
      if (replacedColumns.has(key)) return;
      if (key === fobLocal) {
        next[key] = detail.local_total_price - totalLocal;
      } else if (key === fobUsd) {
        next[key] = detail.local_total_price / exchangeRate - totalUsd;
      } else {
        next[key] = value;
      }
    });
    return next;
  });
};
